import React from 'react'

import {
  Alert,
  Button,
  Confirm,
  DefaultError,
  Pagination,
  ProgressModal,
  Transition,
} from 'src/chrome'
import { useActions, useSelector } from 'src/hooks'

import {
  AddressFormModal,
  ContactActionBar,
  ContactHeader,
  ContactInfo,
  Contacts,
  GroupAddCreateModal,
  SelectCampaignModal,
  ShareContact,
} from '../components'
import styles from './contactManager.module.scss'
import { Steps } from './types'
import Hooks, { PAGE_SIZE } from './hooks'
import suspenseBoundary from '../../chrome/SuspenseBoundary/suspenseBoundaryHOC'
import { RelationshipManagerRoute } from '../routes/RelationshipManagerRoute'
import AnnouncementBanners from 'src/marketing/AnnouncementBanners/AnnouncementBanners'

const ContactManager: React.FC<{}> = () => {
  const hooks = Hooks()
  const { step } = hooks

  const route = useSelector(state => state.route)
  const isNewContactModalOpen =
    route.subroute.path === RelationshipManagerRoute.path
      ? route.subroute.isNewContactModalOpen
      : null

  const actions = useActions()
  const selectedContactIds = hooks.selectedContacts.map(contact => contact.id)
  const selectedContact = hooks.paginatedContacts
    ? hooks.paginatedContacts.find(
        contact => contact.id === selectedContactIds[0],
      )
    : undefined

  return (
    <div id="contact_manager" className={styles.managerContainer}>
      {hooks.isLoading && <Transition message="One Moment..." />}
      <AnnouncementBanners />

      <ContactHeader
        onAdd={actions.toggleIsNewContactModalOpen}
        onOpenGroups={actions.openGroups}
        filters={hooks.search.filters}
        setTerms={hooks.setTerms}
        setFilters={hooks.setFilters}
        onImport={actions.openContactsImporter}
      />

      <ContactActionBar
        title="Contacts"
        totalCount={hooks.contactsCount}
        selectedCount={hooks.selectedContacts.length}
        isViewingGroups={false}
        onToggleSelectAll={hooks.toggleSelectAll}
        onSendCard={() => hooks.sendCard(undefined)}
        onViewGroups={actions.openGroups}
        onStartCampaign={() => hooks.selectCampaign(undefined)}
        onAddToGroup={hooks.addToGroup}
        onDelete={hooks.confirmDelete}
        onShare={hooks.confirmShare}
        onExport={hooks.confirmExport}
        handleEditContact={
          selectedContactIds.length > 1
            ? undefined
            : () => hooks.editContact(selectedContactIds[0])
        }
        handleHistoryClick={
          selectedContactIds.length > 1 ? undefined : hooks.viewHistory
        }
        contact={selectedContact}
      />
      <div className={styles.body}>
        {step.type === Steps.EditContact &&
          (() => {
            const { contactId, groups } = step

            return groups ? (
              <ContactInfo
                contactId={contactId}
                groups={groups}
                onClose={hooks.doneEditing}
                handleSendCardClick={() => hooks.sendCard([contactId])}
                handleCampaignClick={() => hooks.selectCampaign([contactId])}
                goToContact={hooks.editContact}
              />
            ) : (
              <Transition message="Loading contact" />
            )
          })()}

        {step.type === Steps.ConfirmDelete && (
          <Confirm
            title={`Delete ${hooks.selectedContacts.length} contacts`}
            message={'Are you sure you want to delete the selected contacts?'}
            confirmTitle={'Delete'}
            onConfirm={hooks.deleteSelectedContacts}
            onDecline={hooks.resetStep}
          />
        )}
        {isNewContactModalOpen && (
          <AddressFormModal
            contact={undefined}
            close={actions.toggleIsNewContactModalOpen}
            onSubmit={hooks.saveContact}
          />
        )}

        {step.type === Steps.DeleteProgress && (
          <ProgressModal
            size={100}
            isOpen={true}
            currentCount={step.deleted}
            totalCount={hooks.selectedContacts.length || undefined}
            errorTitle={'The following contacts could not be deleted'}
            errorList={step.errors}
            message={'contacts deleted successfully'}
            onClose={step.done ? hooks.resetStep : undefined}
            isDone={step.done}
          />
        )}

        {step.type === Steps.ConfirmShare && (
          <ShareContact
            contactsIds={hooks.selectedContacts.map(contact => contact.id)}
            onClose={hooks.resetStep}
            onSubmit={hooks.shareSelectedContacts}
          />
        )}

        {step.type === Steps.SelectCampaign && (
          <SelectCampaignModal
            isOpen={true}
            onClose={hooks.resetStep}
            onSubmit={campaign => hooks.sendCampaign(campaign, step.recipients)}
          />
        )}
        {step.type === Steps.AddToGroup &&
          (hooks.groups ? (
            <GroupAddCreateModal
              isOpen={true}
              onClose={hooks.resetStep}
              onCreateGroup={hooks.createGroup}
              onSelect={hooks.addSelectedContactsToGroup}
              initialMode="select"
              contactsIds={hooks.selectedContacts.map(contact => contact.id)}
              groups={hooks.groups}
            />
          ) : (
            <Transition message="Loading groups..." />
          ))}

        {step.type === Steps.CreateOrder && (
          <Transition message={'Creating order...'} />
        )}

        {step.type === Steps.ExportProgress && (
          <Alert
            isOpen={true}
            onClose={hooks.resetStep}
            type={'info'}
            timeout={7000}
            title={'Exporting to CSV'}
            message={'Your contacts will begin downloading shortly.'}
          />
        )}

        {step.type === Steps.ConfirmExport && (
          <Confirm
            title={'Export contacts'}
            message={
              'Your CSV file will be downloaded by the browser. If you selected a large number of contacts, it will take a few seconds for the download to begin.'
            }
            confirmTitle={'Export'}
            childType={'form'}
            onConfirm={() => hooks.exportContacts()}
            onDecline={hooks.resetStep}
          />
        )}

        {step.type === Steps.ShareProgress && (
          <ProgressModal
            size={100}
            isOpen={true}
            currentCount={step.deleted}
            totalCount={hooks.selectedContacts.length || undefined}
            errorTitle={'The following contacts could not be shared'}
            errorList={step.errors}
            message={'contacts shared successfully'}
            onClose={step.done ? hooks.resetStep : undefined}
            isDone={step.done}
          />
        )}

        {hooks.paginatedContacts.length ? (
          <div className={styles.tableContainer}>
            <Contacts
              isEveryContactSelected={hooks.isEveryContactSelected}
              contacts={hooks.paginatedContacts}
              goToContact={hooks.editContact}
              handleSelectAllClick={hooks.toggleSelectAll}
              onSelect={hooks.toggleSelect}
              selectedContacts={hooks.selectionMap}
            />
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flex: '1 1 auto',
            }}
          >
            <span>No contacts found</span>

            <Button
              title={'Clear Search'}
              buttonColor={'pink'}
              style={{ marginTop: 15 }}
              onClick={hooks.reset}
            />
          </div>
        )}
      </div>
      <div className={styles.pagination}>
        <Pagination
          onPageChange={hooks.setPage}
          count={hooks.contactsCount}
          pagesToShow={5}
          pageSize={PAGE_SIZE}
          currentPage={hooks.page}
        />
      </div>
    </div>
  )
}

export default suspenseBoundary({
  component: ContactManager,
  unresolved: <Transition message={'Loading contacts...'} />,
  failure: DefaultError,
})
